import { useEffect, useRef } from 'react';
import {
  formatProductPagePriceLabel,
  PULSE_CLICK_ACTION,
  trackProductPageClick,
} from './pulseClickIds';

/** Wait after item-detail API + price updates before firing `view_price`. */
export const PRODUCT_PAGE_VIEW_PRICE_DELAY_MS = 1000;

/**
 * Fire `view_price` once per mount after item detail is ready, price is valid,
 * and the value has been stable for {@link PRODUCT_PAGE_VIEW_PRICE_DELAY_MS}.
 */
export function useProductPageViewPrice(
  price: number | string | null | undefined,
  itemDetailReady: boolean,
  enabled = true
): void {
  const hasFiredRef = useRef(false);
  const priceRef = useRef(price);
  priceRef.current = price;

  useEffect(() => {
    if (!enabled || !itemDetailReady || hasFiredRef.current) return;

    const priceLabel = formatProductPagePriceLabel(price);
    if (!priceLabel) return;

    const timer = window.setTimeout(() => {
      if (hasFiredRef.current) return;
      const finalLabel = formatProductPagePriceLabel(priceRef.current);
      if (!finalLabel) return;
      hasFiredRef.current = true;
      trackProductPageClick({
        action: PULSE_CLICK_ACTION.VIEW_PRICE,
        label: finalLabel,
      });
    }, PRODUCT_PAGE_VIEW_PRICE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [enabled, itemDetailReady, price]);
}
